import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  GraduationCap, 
  BookOpen, 
  Calendar,
  IndianRupee,
  Menu, 
  X,
  LogOut,
  ClipboardList,
  Bell,
  FileText
} from 'lucide-react';
import Dashboard from './Dashboard';
import Students from './Students';
import Staff from './Staff';
import Classes from './Classes';
import ClassView from './ClassView';
import ClassEdit from './ClassEdit';
import Attendance from './Attendance';
import TeachersAttendance from './TeachersAttendance';
import Fees from './Fees';
import Homeworks from './Homeworks';
import AppPages from './AppPages';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../features/auth/authSlice';

const logo = process.env.PUBLIC_URL + '/logo.png';


const DashboardScreen = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Removed unused openDropdown state
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const navigation = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'classes', name: 'Classes', icon: BookOpen },
    { id: 'teachers', name: 'Staff', icon: GraduationCap },
    { id: 'students', name: 'Students', icon: Users },
    { id: 'attendance', name: 'Attendance', icon: Calendar },
    { id: 'homeworks', name: 'Homework', icon: ClipboardList },
    { id: 'fees', name: 'Fees', icon: IndianRupee },
    { id: 'noticeboard', name: 'Notice Board', icon: Bell }, 
    { id: 'biometric-devices', name: 'Biometric Devices', icon: ClipboardList },
    { id: 'biometric-logs', name: 'Biometric Logs', icon: ClipboardList },
    { id: 'apppages', name: 'App Content', icon: FileText },
  ];

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarOpen(prev => {
      const newState = !prev;
      localStorage.setItem('sidebarOpen', JSON.stringify(newState));
      return newState;
    });
  }, []);

  const handleSidebarClose = useCallback(() => {
    // Persistent state handled by handleSidebarToggle or manual changes
  }, []);

  const handleNavigationClick = useCallback((pageId) => {
    setActivePage(pageId);
    // Removed setIsSidebarOpen(false) to maintain persistence across navigation
  // Removed setOpenDropdown (dropdown logic)
    // Navigate to the appropriate route
    switch (pageId) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'students':
        navigate('/students');
        break;
      case 'teachers':
        navigate('/staff');
        break;
      case 'classes':
        navigate('/classes');
        break;
      case 'attendance':
        navigate('/attendance'); // Now navigates to tabbar wrapper
        break;
      case 'homeworks':
        navigate('/homeworks');
        setActivePage('homeworks');
        break;
      case 'fees':
        navigate('/fees');
        setActivePage('fees');
        break;
      case 'noticeboard':
        navigate('/notice-board');
        setActivePage('noticeboard');
        break;
      case 'apppages':
        navigate('/app-pages');
        setActivePage('apppages');
        break;
      case 'biometric-devices':
        navigate('/biometric/devices');
        setActivePage('biometric-devices');
        break;
      case 'biometric-logs':
        navigate('/biometric/logs');
        setActivePage('biometric-logs');
        break;
      default:
        navigate('/dashboard');
    }
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    setShowLogoutModal(true);
  }, []);

  const handleLogoutConfirm = useCallback(() => {
    // Dispatch logout action to reset Redux state
    dispatch(logout());
    
    // Clear all localStorage
    localStorage.clear();
    
    // Navigate to login page
    navigate('/login');
  }, [dispatch, navigate]);

  const handleLogoutCancel = useCallback(() => {
    setShowLogoutModal(false);
  }, []);

  // Sync sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Update active page based on current route
  useEffect(() => {
    if (location.pathname.startsWith('/classes/')) {
      setActivePage('classes');
    } else if (location.pathname === '/students') {
      setActivePage('students');
    } else if (location.pathname === '/staff') {
      setActivePage('teachers');
    } else if (location.pathname === '/classes') {
      setActivePage('classes');
    } else if (location.pathname.startsWith('/attendance/')) {
      setActivePage('attendance');
    } else if (location.pathname === '/attendance') {
      setActivePage('attendance');
    } else if (location.pathname === '/homeworks') {
      setActivePage('homeworks');
    } else if (location.pathname === '/fees') {
      setActivePage('fees');
    } else if (location.pathname === '/notice-board') {
      setActivePage('noticeboard');
    } else if (location.pathname === '/app-pages') {
      setActivePage('apppages');
    } else if (location.pathname === '/biometric/devices') {
      setActivePage('biometric-devices');
    } else if (location.pathname === '/biometric/logs') {
      setActivePage('biometric-logs');
    } else if (location.pathname === '/dashboard' || location.pathname === '/') {
      setActivePage('dashboard');
    }
  }, [location.pathname]);

  const renderContent = () => {
    // If children are provided (from route wrappers), render them
    if (children) {
      return children;
    }

    // Check if we're on a class-specific route
    if (location.pathname.startsWith('/classes/')) {
      if (location.pathname.endsWith('/edit')) {
        return <ClassEdit />;
      } else {
        return <ClassView />;
      }
    }

    // Handle attendance routes
    if (location.pathname.startsWith('/attendance/')) {
      if (location.pathname === '/attendance/students') {
        return <Attendance />; // Students attendance (existing)
      } else if (location.pathname === '/attendance/teachers') {
        return <TeachersAttendance />; // Teachers attendance (new)
      }
    }

    // Default dashboard navigation based on URL
    if (location.pathname === '/students') {
      return <Students />;
    } else if (location.pathname === '/staff') {
      return <Staff />;
    } else if (location.pathname === '/classes') {
      return <Classes />;
    } else if (location.pathname === '/attendance') {
      return <Attendance />; // Default to students attendance
    } else if (location.pathname === '/fees') {
      return <Fees />;
    } else if (location.pathname === '/homeworks') {
      return <Homeworks />;
    } else {
      // Handle dashboard tabs
      if (activePage === 'fees') {
        return <Fees />;
      }
      if (activePage === 'homeworks') {
        return <Homeworks />;
      }
      if (activePage === 'noticeboard') {
        // Since notice-board route is defined in App.js with <DashboardScreen><NoticeBoard/></DashboardScreen>
        // we can either return children here (which it does at the start of renderContent)
        // or just return children directly.
        // It's covered by `if (children) return children;` at the top of renderContent.
      }
      return <Dashboard />;
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar - Fixed width with independent scroll */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-white shadow-xl transition-all duration-300 ease-in-out flex flex-col ${
        isSidebarOpen ? 'w-64' : 'w-20'
      }`}>
        <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200">
          <div className="flex items-center justify-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center transform transition-all duration-300 hover:scale-110 cursor-pointer">
              <img 
                src={logo}
                alt="SmartCampusz Logo" 
                className="w-10 h-10 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </div>
            {isSidebarOpen && (
              <span className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent transition-opacity duration-300">SmartCampusz</span>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-4">
          <ul className="space-y-2">
            {navigation.map((item) => {
              const IconComponent = item.icon;
              const isActive = activePage === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigationClick(item.id)}
                    className={`w-full flex items-center ${isSidebarOpen ? 'px-4' : 'justify-center px-2'} py-3 text-left rounded-xl transition-all duration-300 group ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-50 to-teal-50 text-blue-700 shadow-md border border-blue-100'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={!isSidebarOpen ? item.name : ''}
                  >
                    <IconComponent size={20} className={`transition-all duration-300 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-500'} ${!isSidebarOpen ? 'mx-auto' : ''}`} />
                    {isSidebarOpen && (
                      <span className="ml-3 font-medium transition-opacity duration-300">{item.name}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Profile Section - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          {isSidebarOpen ? (
            <>
              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg transition-all duration-300 hover:shadow-md cursor-pointer">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{user?.adminName?.charAt(0) || 'A'}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-900">{user?.adminName}</p>
                  <p className="text-xs text-gray-600">School Administrator</p>
                </div>
              </div>
              <button
                onClick={handleLogoutClick}
                className="w-full mt-3 flex items-center space-x-3 px-4 py-3 text-left rounded-xl transition-all duration-300 text-red-600 hover:bg-red-50 hover:shadow-sm border border-transparent hover:border-red-200"
              >
                <LogOut size={20} className="text-red-500" />
                <span className="font-medium">Logout</span>
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center cursor-pointer">
                <span className="text-white font-semibold text-sm">{user?.adminName?.charAt(0) || 'A'}</span>
              </div>
              <button
                onClick={handleLogoutClick}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                title="Logout"
              >
                <LogOut size={20} className="text-red-500" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* No overlay needed for collapsible sidebar */}

      {/* Main Content - Independent scroll area */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        isSidebarOpen ? 'ml-64' : 'ml-20'
      }`}>
        {/* Top Header with Menu Button - Always visible */}
        <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 px-4 h-16 flex items-center">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSidebarToggle}
                className="p-2 text-gray-400 hover:text-gray-600 transition-all duration-300 hover:scale-110"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Page Content - Scrollable */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-6 py-8 max-w-7xl">
            {children || renderContent()}
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Logout</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to logout? You will need to login again to access the system.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleLogoutCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardScreen;